import { EmployeeGroupFragment } from 'generated/resources/graphql'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'

import i18n from 'modules/core/lib/i18n'
import type { Collection, Model } from 'modules/core/lib/mobx-rest'
import legalEntities from 'modules/core/store/collections/legalEntities'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { currentContractVersions } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import levels from 'modules/job_catalog/store/collections/levels'
import roles from 'modules/job_catalog/store/collections/roles'
import locations from 'modules/locations/store/collections/locations'
import type Location from 'modules/locations/store/models/Location'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import type Team from 'modules/teams/store/models/Team'

import { EmployeeStatusOption } from '../EmployeeMultiFilters/commonOptions'
import employeeStatuses from '../EmployeeMultiFilters/hooks/utils/employeeStatuses'

function sortedBy<T extends Model = any>(propName: string, toSort: Collection<T>) {
  return sortBy<T>(toSort.models, (model: T) => model.attributes[propName])
}

export const teamOptions = async () => {
  await teams.fetch({ withCachedFetchParams: true })
  // NOTE: we need to fetch memberships to be able to filter by team
  await memberships.fetch({ withCachedFetchParams: true })

  return sortedBy<Team>('name', teams).map((team) => ({
    value: team.attributes.id,
    label: team.attributes.name,
  }))
}

export const locationOptions = async () => {
  await locations.fetch({ withCachedFetchParams: true })

  return sortedBy<Location>('name', locations).map((location) => ({
    value: location.attributes.id,
    label: location.attributes.name,
  }))
}

export const legalEntityOptions = async () => {
  await legalEntities.fetch({ withCachedFetchParams: true })

  return sortedBy<LegalEntity>('legal_name', legalEntities).map((legalEntity) => ({
    value: legalEntity.attributes.id,
    label: legalEntity.attributes.legal_name,
  }))
}

const clockinStatusOptions = async (): Promise<EmployeeStatusOption[]> => {
  const items = [
    {
      value: employeeStatuses.missingClockIn,
      label: i18n.t('filters.multi_filters.missing_clockin'),
    },
    {
      value: employeeStatuses.clockIn,
      label: i18n.t('filters.filter.clocked_in'),
    },
    {
      value: employeeStatuses.onBreak,
      label: i18n.t('filters.filter.taking_break'),
    },
  ]

  return sortBy(items, ['label'])
}

const alertsOptions = async (): Promise<EmployeeStatusOption[]> => {
  const items = [
    {
      value: employeeStatuses.alertsAllTypes,
      label: i18n.t('filters.multi_filters.alerts.options.all'),
    },
    {
      value: employeeStatuses.forgotToClockIn,
      label: i18n.t('filters.multi_filters.alerts.options.missing_clock_in'),
    },
    {
      value: employeeStatuses.missingClockOut,
      label: i18n.t('filters.multi_filters.alerts.options.missing_clock_out'),
    },
    {
      value: employeeStatuses.missingBreakTime,
      label: i18n.t('filters.multi_filters.alerts.options.missing_break_time'),
    },
    {
      value: employeeStatuses.insufficientBreak,
      label: i18n.t('filters.multi_filters.alerts.options.insufficient_break'),
    },
  ]

  return sortBy(items, ['label'])
}

const getJobTitle = (contract: ContractVersion) => contract.attributes.job_title || ''

export const jobTitleOptions = async () => {
  await contractVersions.fetch({ withCachedFetchParams: true })

  const contracts = uniqBy(currentContractVersions(contractVersions), getJobTitle)

  const sortedContracts = sortBy(contracts, getJobTitle)

  return sortedContracts.map((contract) => ({
    value: getJobTitle(contract),
    label: getJobTitle(contract),
  }))
}

export const jobCatalogTitleOptions = async () => {
  await levels.fetch({ withCachedFetchParams: true })
  await roles.fetch({ withCachedFetchParams: true })

  return roles.active.map((x) => ({
    value: x.attributes.name,
    label: x.attributes.name,
  }))
}

export const employeeGroupOptions = (employeeGroups: EmployeeGroupFragment[]) => {
  return employeeGroups.map((group) => ({
    value: group.id,
    label: group.name,
  }))
}
